/**
 * /worldview/analyzer — The Analyzer onboarding flow (Phase 1).
 *
 * Orchestrates Stage 0 (framing) → Stage 1 (forced-choice gauntlet) →
 * Stage 5 (test debate). State persists to localStorage via draftStorage
 * after every meaningful interaction so the user can pause and resume.
 *
 * On Save: writes a StructuredWorldview to the new store AND mirrors a
 * legacy SavedWorldview (markdown blob form) so the existing library
 * surfaces (chat, watch, spar) keep working unchanged during migration.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { OnboardingDraft, StructuredWorldview } from '@arena/shared';
import { SceneBack } from '@/shared/components/SceneBack';
import { sampleQuestions, questionById } from '../data/question-bank';
import {
  clearDraft,
  createDraft,
  loadDraft,
  saveDraft,
} from '../lib/draftStorage';
import {
  defaultContract,
  saveStructuredWorldview,
} from '../lib/structuredWorldviewStorage';
import {
  assembleSystemPrompt,
  buildPreviewWorldview,
} from '../lib/systemPromptAssembler';
import { saveWorldview } from '../lib/storage';
import { Stage0Frame } from '../components/onboarding/Stage0Frame';
import { Stage1ForcedChoice } from '../components/onboarding/Stage1ForcedChoice';
import { Stage15AnchorFacts } from '../components/onboarding/Stage15AnchorFacts';
import { Stage5TestDebate } from '../components/onboarding/Stage5TestDebate';

const PHASE1_QUESTION_COUNT = 30;

export function Analyzer() {
  const navigate = useNavigate();
  const [draft, setDraft] = useState<OnboardingDraft | null>(() => loadDraft());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.title = 'The Analyzer — The Arena';
  }, []);

  // Initialize a fresh draft if none exists or the existing one is stale.
  const ensureDraft = (name?: string): OnboardingDraft => {
    if (draft && draft.stage1.questionIds.length > 0) return draft;
    const sampled = sampleQuestions(PHASE1_QUESTION_COUNT).map((q) => q.id);
    const fresh = createDraft(sampled);
    if (name) fresh.name = name;
    setDraft(fresh);
    saveDraft(fresh);
    return fresh;
  };

  const updateDraft = (updater: (d: OnboardingDraft) => OnboardingDraft) => {
    setDraft((curr) => {
      if (!curr) return curr;
      const next = updater(curr);
      saveDraft(next);
      return next;
    });
  };

  // ── Stage 0 → Stage 1 ─────────────────────────────────────────────────
  const handleBegin = (name: string) => {
    const base = ensureDraft(name);
    updateDraft(() => ({ ...base, name, stage: 1 }));
  };

  // ── Stage 1 answer + advance ──────────────────────────────────────────
  const handleAnswer = (
    questionId: string,
    next: { choice: string; why: string; hardness?: 1 | 2 | 3 },
  ) => {
    updateDraft((d) => ({
      ...d,
      stage1: {
        ...d.stage1,
        answers: { ...d.stage1.answers, [questionId]: next },
      },
    }));
  };

  const handleStage1Complete = () => {
    // Advance to Stage 1.5 (anchor facts) before the test debate. The
    // identity layer is load-bearing for personal questions and should
    // be captured before the user judges the agent.
    updateDraft((d) => ({ ...d, stage: 1.5 }));
  };

  // ── Stage 1.5 (anchor facts) → Stage 5 ────────────────────────────────
  const handleAnchorFactsContinue = (anchorFacts: string[]) => {
    updateDraft((d) => ({
      ...d,
      stage15: { anchorFacts, skipped: false },
      stage: 5,
    }));
  };

  const handleAnchorFactsSkip = () => {
    updateDraft((d) => ({
      ...d,
      stage15: { anchorFacts: [], skipped: true },
      stage: 5,
    }));
  };

  const handleAnchorFactsBack = () => {
    updateDraft((d) => ({ ...d, stage: 1 }));
  };

  // ── Stage 5 save / refine / exit ──────────────────────────────────────
  const previewWorldview: StructuredWorldview | null = useMemo(() => {
    if (!draft || !draft.name) return null;
    const contract = defaultContract(draft.name);
    return buildPreviewWorldview(
      draft.name,
      draft.stage1,
      (id) => {
        const q = questionById(id);
        return q ? { scenario: q.scenario, domain: q.domain } : undefined;
      },
      contract,
      draft.stage15?.anchorFacts,
    );
  }, [draft]);

  const handleSave = () => {
    if (!draft || !draft.name || !previewWorldview || saving) return;
    setSaving(true);
    try {
      // Use the preview's contract — it already has anchor facts folded in
      // from the draft. Calling defaultContract() again would lose them.
      const saved = saveStructuredWorldview({
        name: draft.name,
        contract: previewWorldview.contract,
        cases: previewWorldview.cases,
        voiceCorpus: [],
      });

      // Mirror as a legacy SavedWorldview (markdown blob) so the existing
      // library/chat surfaces find it. The "markdown" body is just the
      // assembled system prompt — it'll display in the legacy chat using
      // the existing system-prompt wrap.
      const assembled = assembleSystemPrompt({ worldview: previewWorldview });
      const markdownBody = `# ${draft.name}\n\n_(Generated by The Analyzer — case-based worldview)_\n\n\`\`\`\n${assembled}\n\`\`\`\n`;
      saveWorldview(markdownBody);

      clearDraft();
      setDraft(null);
      navigate('/worldview', { state: { justCreated: saved.id } });
    } catch (err) {
      console.error('Save failed', err);
      setSaving(false);
    }
  };

  const handleRefineFromStage5 = () => {
    // Go back to Stage 1 with answers intact so the user can tune.
    updateDraft((d) => ({ ...d, stage: 1 }));
  };

  const handleExit = () => {
    // Keep the draft in localStorage so the user can resume; just navigate away.
    navigate('/worldview');
  };

  const handleDiscard = () => {
    clearDraft();
    setDraft(null);
    navigate('/worldview');
  };

  // ── Render ────────────────────────────────────────────────────────────
  const stage = draft?.stage ?? 0;

  return (
    <main className="scene scene--analyzer">
      <div
        className="scene__image scene__image--worldview"
        role="img"
        aria-label="The Library"
      />
      <div className="scene__vignette" />
      <SceneBack />

      <div className="analyzer">
        {stage === 0 && (
          <Stage0Frame
            initialName={draft?.name}
            onBegin={handleBegin}
            onCancel={handleExit}
            questionCount={PHASE1_QUESTION_COUNT}
          />
        )}

        {stage === 1 && draft && (
          <Stage1ForcedChoice
            questionIds={draft.stage1.questionIds}
            answers={draft.stage1.answers}
            onAnswer={handleAnswer}
            onComplete={handleStage1Complete}
            onExit={handleExit}
          />
        )}

        {stage === 1.5 && draft && draft.name && (
          <Stage15AnchorFacts
            initial={draft.stage15?.anchorFacts}
            worldviewName={draft.name}
            onContinue={handleAnchorFactsContinue}
            onSkip={handleAnchorFactsSkip}
            onBack={handleAnchorFactsBack}
          />
        )}

        {stage === 5 && draft && draft.name && previewWorldview && (
          <Stage5TestDebate
            worldviewName={draft.name}
            preview={previewWorldview}
            onSave={handleSave}
            onRefine={handleRefineFromStage5}
            onExit={handleDiscard}
          />
        )}

        {stage === 'complete' && (
          <div className="analyzer__done">
            <p>Saved. Redirecting…</p>
          </div>
        )}
      </div>
    </main>
  );
}
